/** El logotipo de Essity.
 *
 * NO se reproduce a mano. El logo es propiedad del cliente y una imitación aproximada se ve peor
 * que no ponerlo: la roseta de ocho «e» tiene proporciones y rotaciones exactas que no se
 * adivinan desde una captura. Así que el componente busca el archivo OFICIAL en
 * `public/marca/essity.svg` (y `essity-simbolo.svg` para la versión compacta) y, mientras no
 * esté, dibuja una marca sobria con los dos colores de la guía —que es una marca de la agencia,
 * no una falsificación del logo—.
 *
 * Para poner el oficial: copiar los SVG en `frontend/public/marca/` con esos nombres. No hay que
 * tocar código.
 */
import { useEffect, useState } from "react";

type Estado = "buscando" | "oficial" | "propio";

function useLogoOficial(archivo: string): Estado {
  const [estado, setEstado] = useState<Estado>("buscando");
  useEffect(() => {
    let vivo = true;
    fetch(`/marca/${archivo}`, { method: "HEAD" })
      .then((r) => {
        if (!vivo) return;
        const tipo = r.headers.get("content-type") ?? "";
        setEstado(r.ok && tipo.includes("svg") ? "oficial" : "propio");
      })
      .catch(() => vivo && setEstado("propio"));
    return () => { vivo = false; };
  }, [archivo]);
  return estado;
}

/** El símbolo compacto, para la esquina y el avatar. */
export function Simbolo({ tamano = 26, titulo = "Essity" }: { tamano?: number; titulo?: string }) {
  const estado = useLogoOficial("essity-simbolo.svg");
  if (estado === "oficial") {
    return <img src="/marca/essity-simbolo.svg" alt={titulo} width={tamano} height={tamano} className="simbolo" />;
  }
  // Marca propia: dos rombos entrelazados con los colores de la guía. Deliberadamente distinta
  // de la roseta de Essity, para que nadie la confunda con el logo.
  return (
    <svg width={tamano} height={tamano} viewBox="0 0 24 24" className="simbolo" role="img" aria-label={titulo}>
      <rect width="24" height="24" rx="7" fill="var(--marca-azul)" />
      <path d="M12 5.5 17 12l-5 6.5L7 12z" fill="var(--marca-magenta)" />
      <circle cx="12" cy="12" r="1.9" fill="var(--marca-blanco)" />
    </svg>
  );
}

/** El logotipo con palabra, para la cabecera de la barra lateral. */
export function Logo({ alto = 22 }: { alto?: number }) {
  const estado = useLogoOficial("essity.svg");
  if (estado === "oficial") {
    return <img src="/marca/essity.svg" alt="Essity" height={alto} className="logo-oficial" />;
  }
  return (
    <span className="logo-propio">
      <Simbolo tamano={alto + 4} titulo="Essity Consumer Tissue" />
      <span className="logo-texto">
        <strong>essity</strong>
        <em>Consumer Tissue</em>
      </span>
    </span>
  );
}
